import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api } from '../api/client';

type Provider = 'log' | 'suppress' | 'smtp';

interface EmailConfig {
  provider: Provider;
  delivers: boolean;
  source: 'environment' | 'database';
  editable: boolean;
  from: { address: string; name: string };
  smtp: {
    host: string | null;
    port: number;
    secure: boolean;
    user: string | null;
    passwordSet: boolean;
    timeoutSeconds: number;
  };
}

interface TestResult {
  ok: boolean;
  stage: string;
  message: string;
  providerRef?: string | null;
  detail?: string;
}

interface Draft {
  provider: Provider;
  fromAddress: string;
  fromName: string;
  host: string;
  port: string;
  secure: boolean;
  user: string;
  password: string;
  timeoutSeconds: string;
}

function draftFrom(config: EmailConfig): Draft {
  return {
    provider: config.provider,
    fromAddress: config.from.address,
    fromName: config.from.name,
    host: config.smtp.host ?? '',
    port: String(config.smtp.port),
    secure: config.smtp.secure,
    user: config.smtp.user ?? '',
    // Never populated from the server: the stored password is not readable,
    // and an empty box here means "leave it as it is".
    password: '',
    timeoutSeconds: String(config.smtp.timeoutSeconds),
  };
}

/**
 * Email delivery: what is configured, how to change it, and whether it works.
 *
 * The settings start in the environment and can be overridden here, because
 * the moment you discover the host is wrong is the moment you need to fix it,
 * not the next deployment. The password is the one value that only travels
 * one way — it is encrypted before it is stored and never sent back — so the
 * field is left blank and an empty box means "keep the one you have".
 */
export function EmailSettings() {
  const [config, setConfig] = useState<EmailConfig | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [editing, setEditing] = useState(false);
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState<'check' | 'send' | 'save' | 'reset' | null>(null);
  const [result, setResult] = useState<TestResult | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const load = useCallback(() => {
    api<EmailConfig>('/admin/notifications/email-config')
      .then((next) => {
        setConfig(next);
        setDraft(draftFrom(next));
      })
      .catch(() => setConfig(null));
  }, []);

  useEffect(load, [load]);

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  }

  async function run(withAddress: boolean, event?: FormEvent) {
    event?.preventDefault();
    setBusy(withAddress ? 'send' : 'check');
    setResult(null);
    try {
      const body = withAddress ? JSON.stringify({ to }) : JSON.stringify({});
      setResult(await api<TestResult>('/admin/notifications/test-email', { method: 'POST', body }));
    } catch (caught) {
      setResult({
        ok: false,
        stage: 'request',
        message: (caught as Error).message || 'The test could not be run.',
      });
    } finally {
      setBusy(null);
    }
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!draft) return;
    setBusy('save');
    setResult(null);
    setSaved(null);
    try {
      const payload: Record<string, unknown> = {
        provider: draft.provider,
        fromAddress: draft.fromAddress.trim(),
        fromName: draft.fromName.trim(),
        host: draft.host.trim(),
        port: Number(draft.port) || 587,
        secure: draft.secure,
        user: draft.user.trim(),
        timeoutSeconds: Number(draft.timeoutSeconds) || 15,
      };
      // Only send the password when one was typed, so saving the rest of the
      // form does not wipe a password nobody can see to re-enter.
      if (draft.password) payload.password = draft.password;

      const next = await api<EmailConfig>('/admin/notifications/email-config', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      setConfig(next);
      setDraft(draftFrom(next));
      setEditing(false);
      setSaved('Settings saved. They take effect immediately — no restart needed.');
    } catch (caught) {
      setResult({ ok: false, stage: 'save', message: (caught as Error).message });
    } finally {
      setBusy(null);
    }
  }

  async function reset() {
    setBusy('reset');
    setResult(null);
    setSaved(null);
    try {
      const next = await api<EmailConfig>('/admin/notifications/email-config', {
        method: 'DELETE',
      });
      setConfig(next);
      setDraft(draftFrom(next));
      setEditing(false);
      setSaved('Saved settings discarded. The environment is in force again.');
    } catch (caught) {
      setResult({ ok: false, stage: 'reset', message: (caught as Error).message });
    } finally {
      setBusy(null);
    }
  }

  if (!config || !draft) return null;

  const smtpSelected = draft.provider === 'smtp';

  // Signing in as one address and sending as another is what mail servers
  // refuse most often, and it is invisible until the first send fails — so
  // say so while the two fields are still on screen together.
  const sendAsMismatch =
    smtpSelected &&
    draft.user.trim().length > 0 &&
    draft.fromAddress.trim().toLowerCase() !== draft.user.trim().toLowerCase();

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card__head">
        <span className="card__title">Email delivery</span>
        <span className="smtp__head">
          <span className={`pill pill--${config.delivers ? 'approved' : 'pending'}`}>
            {config.delivers ? 'SMTP' : config.provider}
          </span>
          {!editing ? (
            <button className="btn btn--sm" onClick={() => setEditing(true)}>
              Edit settings
            </button>
          ) : null}
        </span>
      </div>

      <div className="card__body">
        {!config.delivers && !editing ? (
          <p className="muted" style={{ marginTop: 0 }}>
            Nothing is being delivered. Messages are composed, queued and recorded, but delivery is
            set to <strong>{config.provider}</strong> so they never leave. Choose SMTP and give a
            host to send for real.
          </p>
        ) : null}

        {editing ? (
          <form onSubmit={save} noValidate>
            <div className="field">
              <label className="field__label" htmlFor="sx-provider">
                Delivery
              </label>
              <select
                className="field__input"
                id="sx-provider"
                value={draft.provider}
                onChange={(event) => set('provider', event.target.value as Provider)}
              >
                <option value="smtp">SMTP — deliver for real</option>
                <option value="suppress">Suppress — record but never send</option>
                <option value="log">Log — write to the server log (development only)</option>
              </select>
            </div>

            <div className="field__row">
              <div className="field" style={{ flex: 2 }}>
                <label className="field__label" htmlFor="sx-from-address">
                  From address
                </label>
                <input
                  className="field__input"
                  id="sx-from-address"
                  type="email"
                  required
                  value={draft.fromAddress}
                  onChange={(event) => set('fromAddress', event.target.value)}
                  aria-describedby="sx-from-hint"
                />
                {sendAsMismatch ? (
                  <p className="field__hint field__hint--warn" id="sx-from-hint">
                    This is not the address you sign in with. Most mail servers refuse to send
                    under an address the account does not own — use{' '}
                    <strong>{draft.user.trim()}</strong> unless it has been granted “Send As”
                    rights for this one.
                  </p>
                ) : null}
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label className="field__label" htmlFor="sx-from-name">
                  From name
                </label>
                <input
                  className="field__input"
                  id="sx-from-name"
                  required
                  value={draft.fromName}
                  onChange={(event) => set('fromName', event.target.value)}
                />
              </div>
            </div>

            {smtpSelected ? (
              <>
                <div className="field__row">
                  <div className="field" style={{ flex: 2 }}>
                    <label className="field__label" htmlFor="sx-host">
                      Host
                    </label>
                    <input
                      className="field__input"
                      id="sx-host"
                      placeholder="smtp.office365.com"
                      value={draft.host}
                      onChange={(event) => set('host', event.target.value)}
                    />
                  </div>
                  <div className="field" style={{ flex: 1 }}>
                    <label className="field__label" htmlFor="sx-port">
                      Port
                    </label>
                    <input
                      className="field__input"
                      id="sx-port"
                      inputMode="numeric"
                      value={draft.port}
                      onChange={(event) => set('port', event.target.value)}
                    />
                  </div>
                </div>

                <div className="field">
                  <label className="field__check" htmlFor="sx-secure">
                    <input
                      id="sx-secure"
                      type="checkbox"
                      checked={draft.secure}
                      onChange={(event) => set('secure', event.target.checked)}
                    />
                    <span>Implicit TLS</span>
                  </label>
                  <p className="field__hint">
                    On for port 465. Leave it off for port 587, which upgrades with STARTTLS.
                  </p>
                </div>

                <div className="field__row">
                  <div className="field" style={{ flex: 1 }}>
                    <label className="field__label" htmlFor="sx-user">
                      Username
                    </label>
                    <input
                      className="field__input"
                      id="sx-user"
                      autoComplete="off"
                      value={draft.user}
                      onChange={(event) => set('user', event.target.value)}
                    />
                  </div>
                  <div className="field" style={{ flex: 1 }}>
                    <label className="field__label" htmlFor="sx-password">
                      Password
                    </label>
                    <input
                      className="field__input"
                      id="sx-password"
                      type="password"
                      autoComplete="new-password"
                      placeholder={config.smtp.passwordSet ? 'Unchanged' : 'Not set'}
                      value={draft.password}
                      onChange={(event) => set('password', event.target.value)}
                      aria-describedby="sx-password-hint"
                    />
                    <p className="field__hint" id="sx-password-hint">
                      {config.smtp.passwordSet
                        ? 'Leave blank to keep the stored password.'
                        : 'Stored encrypted; it is never shown again.'}
                    </p>
                  </div>
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="sx-timeout">
                    Timeout (seconds)
                  </label>
                  <input
                    className="field__input"
                    id="sx-timeout"
                    inputMode="numeric"
                    value={draft.timeoutSeconds}
                    onChange={(event) => set('timeoutSeconds', event.target.value)}
                  />
                </div>
              </>
            ) : null}

            <div className="smtp__actions">
              <button className="btn" type="submit" disabled={busy !== null}>
                {busy === 'save' ? 'Saving…' : 'Save settings'}
              </button>
              <button
                className="btn btn--ghost"
                type="button"
                disabled={busy !== null}
                onClick={() => {
                  setDraft(draftFrom(config));
                  setEditing(false);
                }}
              >
                Cancel
              </button>
              {config.source === 'database' ? (
                <button
                  className="btn btn--ghost"
                  type="button"
                  disabled={busy !== null}
                  onClick={() => void reset()}
                >
                  {busy === 'reset' ? 'Discarding…' : 'Use environment values'}
                </button>
              ) : null}
            </div>
          </form>
        ) : (
          <>
            <dl className="smtp__grid">
              <div>
                <dt>From</dt>
                <dd>
                  {config.from.name} &lt;{config.from.address}&gt;
                </dd>
              </div>
              <div>
                <dt>Host</dt>
                <dd className="mono">{config.smtp.host ?? 'not set'}</dd>
              </div>
              <div>
                <dt>Port</dt>
                <dd className="mono">
                  {config.smtp.port} · {config.smtp.secure ? 'implicit TLS' : 'STARTTLS'}
                </dd>
              </div>
              <div>
                <dt>Username</dt>
                <dd className="mono">{config.smtp.user ?? 'none'}</dd>
              </div>
              <div>
                <dt>Password</dt>
                <dd>{config.smtp.passwordSet ? 'Set' : 'Not set'}</dd>
              </div>
              <div>
                <dt>Timeout</dt>
                <dd>{config.smtp.timeoutSeconds}s</dd>
              </div>
            </dl>

            <p className="muted smtp__note">
              {config.source === 'database'
                ? 'Saved here, overriding the environment. The password is stored encrypted and is never shown.'
                : 'Coming from the environment. Edit them to override without a redeploy.'}
            </p>
          </>
        )}

        {saved ? (
          <p className="smtp__result smtp__result--ok" role="status">
            {saved}
          </p>
        ) : null}

        <form className="smtp__test" onSubmit={(event) => void run(true, event)}>
          <button
            type="button"
            className="btn btn--sm btn--ghost"
            onClick={() => void run(false)}
            disabled={busy !== null}
          >
            {busy === 'check' ? 'Checking…' : 'Check connection'}
          </button>

          <div className="smtp__send">
            <input
              className="field__input"
              type="email"
              placeholder="you@example.com"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              aria-label="Send a test message to"
            />
            <button className="btn btn--sm" type="submit" disabled={busy !== null || !to.trim()}>
              {busy === 'send' ? 'Sending…' : 'Send test email'}
            </button>
          </div>
        </form>

        <p className="muted smtp__note">
          Checking the connection sends nothing. Entering an address delivers a real message to
          that inbox.
        </p>

        {result ? (
          <p className={`smtp__result smtp__result--${result.ok ? 'ok' : 'bad'}`} role="status">
            <strong>{result.ok ? 'Success' : 'Failed'}</strong> — {result.message}
            {result.detail ? <span className="smtp__detail">{result.detail}</span> : null}
          </p>
        ) : null}
      </div>
    </div>
  );
}
